import {
  type CSSProperties,
  type ReactNode,
  type Ref,
  useContext,
  useRef,
} from 'react'
import cx from 'classnames'
import { useObservableValue } from '@/hooks/rx-hooks'
import { mediaQueryMatchStream } from '@/utils/browser-util'
import { InternalWizardContext } from '@/components/Wizard/index'
import { useScreenHeaderTotalOffset } from '@/components/ScreenHeader'
import { m, type Variants } from 'motion/react'
import styles from './WizardSidePanel.module.css'
import {
  mergeProps,
  useInteractOutside,
  useLandmark,
  usePress,
} from 'react-aria'
import { useComposedRefs } from '@radix-ui/react-compose-refs'
import { shuffleTransition } from '@/consts/springs'
import { FloatingIconButton } from '@/components/FloatingIconButton'

const offside$ = mediaQueryMatchStream(
  'screen and (width >= 1728px) and (height >= 485px)',
)

const sufficientSpaceForClosedOffside$ = mediaQueryMatchStream(
  'screen and (width >= 1520px) and (height >= 485px)',
)

const variants: Variants = {
  closedOffside: {
    scaleX: 0.85,
    rotateY: '-8deg',
    translateX: '35%',
  },
  closedEntirely: {
    scaleX: 1,
    rotateY: '0deg',
    translateX: '110%',
  },
  open: {
    scaleX: 1,
    rotateY: '0deg',
    translateX: '0%',
    opacity: 1,
  },
}

export interface WizardSidePanelProps {
  children?: ReactNode
  ref?: Ref<HTMLDivElement>
  backgroundColor?: 'primary' | 'secondary'
  positioning?: 'offside' | 'floating' | 'floating-tray'
  'aria-label'?: string
  'aria-labelledby'?: string
  transitionDelay?: number
  /**
   * Whether to stretch the panel to available height.
   */
  stretch?: boolean
  /**
   * How to justify the panel content when {@link stretch} is enabled.
   */
  justify?: 'default' | 'end'
  /**
   * Whether the panel is currently opened.
   */
  isOpen?: boolean
  /**
   * Called when the panel should open.
   */
  onOpenChange?: (isOpen: boolean) => unknown
}

/**
 * Wizard Floating Side Panel
 */
export function WizardSidePanel({
  children,
  ref,
  isOpen = false,
  positioning = 'floating',
  backgroundColor = 'primary',
  transitionDelay,
  onOpenChange,
  stretch,
  justify = 'default',
  ...rest
}: WizardSidePanelProps) {
  const { bottomBarHeight } = useContext(InternalWizardContext)
  const screenHeaderTotalOffset = useScreenHeaderTotalOffset()
  const floatingHeight = `calc(100vh - ${bottomBarHeight}px - ${screenHeaderTotalOffset}px)`
  const refObject = useRef<HTMLElement>(null)
  const composedRef = useComposedRefs(ref, refObject)

  const sufficientSpaceForClosedOffside = useSufficientSpaceForClosedOffside()

  const isPressToOpenEnabled =
    !isOpen && onOpenChange && sufficientSpaceForClosedOffside

  const { landmarkProps } = useLandmark({ role: 'region', ...rest }, refObject)

  const { pressProps } = usePress({
    onPress: () => onOpenChange?.(true),
    ref: refObject,
  })

  useInteractOutside({
    isDisabled: !isOpen || !onOpenChange,
    onInteractOutside: () => onOpenChange?.(false),
    ref: refObject,
  })

  const openState = isOpen
    ? 'open'
    : positioning === 'floating-tray'
      ? 'closedEntirely'
      : positioning === 'offside' || sufficientSpaceForClosedOffside
        ? 'closedOffside'
        : 'closedEntirely'

  return (
    <m.div
      layoutRoot
      inert={openState === 'closedEntirely'}
      className={cx(
        styles.root,
        (positioning === 'floating' || positioning === 'floating-tray') &&
          styles.floating,
        openState === 'closedOffside' && styles.closedOffside,
        isPressToOpenEnabled && styles.disableInteractions,
        justify === 'end' && styles.justifyEnd,
      )}
      style={
        {
          '--floating-height': floatingHeight,
          '--floating-top-offset': `${screenHeaderTotalOffset}px`,
        } as CSSProperties
      }
    >
      {/* @ts-expect-error: there's some issue with the props type merging here, However, since we're not using those, this is safe to ignore here. */}
      <m.section
        {...mergeProps(
          landmarkProps,
          isPressToOpenEnabled ? pressProps : undefined,
        )}
        ref={composedRef}
        transition={{
          ...shuffleTransition,
          delay: transitionDelay,
        }}
        className={cx(
          styles.panel,
          backgroundColor === 'secondary' && styles.secondary,
          isPressToOpenEnabled && styles.pressable,
          stretch && styles.stretch,
        )}
        initial={false}
        variants={variants}
        animate={openState}
      >
        {children}
      </m.section>
    </m.div>
  )
}

interface WizardSidePanelScrollerProps {
  children?: ReactNode
  ref?: Ref<HTMLDivElement>
}
/**
 * Wizard Side Panel Scroller
 */
export function WizardSidePanelScroller({
  children,
  ref,
}: WizardSidePanelScrollerProps) {
  return (
    <div ref={ref} className={styles.scroller}>
      {children}
    </div>
  )
}

interface WizardSidePanelFloatingCloseButtonProps {
  onPress?: () => unknown
  layout?: boolean
  layoutId?: string
  transitionDelay?: number
}

/**
 * Wizard Side Panel Header Close Button
 */
export function WizardSidePanelFloatingCloseButton({
  onPress,
  layout,
  layoutId,
  transitionDelay,
}: WizardSidePanelFloatingCloseButtonProps) {
  return (
    <div className={styles.floatingCloseButtonContainer}>
      <FloatingIconButton
        layout={layout && 'position'}
        initial={false}
        layoutId={layoutId}
        label="Close"
        name="close"
        onPress={onPress}
        transition={{
          delay: transitionDelay,
        }}
      />
    </div>
  )
}

/**
 * Checks whether there's enough space to render the side panel in
 * `offside` mode.
 */
export function useIsWizardSidePanelOffside() {
  return useObservableValue(offside$)
}

/**
 * Checks whether there's enough space to render the closed side panel
 * off to the side without it overlaying the main content.
 */
function useSufficientSpaceForClosedOffside() {
  return useObservableValue(sufficientSpaceForClosedOffside$)
}
